import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Plot from 'react-plotly.js';
import { NavigationContext } from '../context/NavigationContext';
import { ChatContext } from '../context/ChatContext';
import useWindowResize from '../hooks/useWindowResize';
import API_URL from '../config';

const MicroAnnotation = () => {
  const { recordingId, epochId, annotationType, start, end, channel } = useParams();
  const navigate = useNavigate();
  const { updateLevel } = useContext(NavigationContext);
  const { setChatDictionary } = useContext(ChatContext);
  const windowSize = useWindowResize();
  
  // State for the annotation data
  const [annotation, setAnnotation] = useState(null);
  const [epochData, setEpochData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Sampling frequency
  const sf = 50;
  
  useEffect(() => {
    updateLevel('micro-annotation', { 
      label: `${annotationType} (${channel || 'global'})`, 
      route: `/analysis/${recordingId}/epoch/${epochId}/annotation/${annotationType}/${start}/${end}${channel ? `/${channel}` : ''}`
    });
  }, [epochId, annotationType, start, end, channel, updateLevel]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/epochs/${epochId}/`);
        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }
        const data = await response.json();
        setEpochData(data);
        
        // Find the specific annotation
        const annotations = channel 
          ? data.annotations.by_channel[channel] || []
          : data.annotations.global || [];
          
        const targetAnnotation = annotations.find(
          ann => ann.annotation_type === annotationType &&
                Math.abs(ann.start - parseFloat(start)) < 0.01 &&
                Math.abs(ann.end - parseFloat(end)) < 0.01
        );
        
        setAnnotation(targetAnnotation);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [epochId, annotationType, start, end, channel]);

  // Update chat context
  useEffect(() => {
    if (annotation && epochData) {
      const chatDictionary = {
        level: 'micro-annotation',
        annotationType: annotationType,
        channel: channel,
        annotation: annotation,
        epochData: epochData
      };
      setChatDictionary(chatDictionary);
    }
  }, [annotation, epochData, annotationType, channel, setChatDictionary]);

  const handleDelete = async () => {
    if (!annotation) return;
    
    if (!window.confirm('Are you sure you want to delete this annotation?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/annotations/${annotation.id}/`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete annotation');
      }

      // Navigate back to the epoch view
      navigate(`/analysis/${recordingId}/epoch/${epochId}`);
    } catch (err) {
      console.error('Error deleting annotation:', err);
      alert('Failed to delete annotation: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 p-4">
        Error loading annotation data: {error.message}
      </div>
    );
  }

  if (!annotation || !epochData) {
    return (
      <div className="text-gray-500 p-4">
        Annotation not found
      </div>
    );
  }

  // Find the relevant signal data
  const signal = channel 
    ? epochData.signals.find(s => s.signal_name === channel)
    : epochData.signals[0];

  let signalData = [];
  if (signal) {
    try {
      signalData = typeof signal.signal_data === 'string' 
        ? JSON.parse(signal.signal_data)
        : signal.signal_data;
    } catch (err) {
      console.error("Error parsing signal data", err);
    }
  }

  return (
    <div className="p-6 bg-white rounded shadow">
      <div className="mb-4">
        <h1 className="text-2xl font-bold mb-2">
          {annotationType} Annotation
        </h1>
        <div className="text-sm text-gray-600">
          <div>Channel: {channel || 'Global'}</div>
          <div>Start: {annotation.start.toFixed(2)}s</div>
          <div>End: {annotation.end.toFixed(2)}s</div>
          <div>Duration: {(annotation.end - annotation.start).toFixed(2)}s</div>
        </div>
      </div>

      {/* Signal Plot */}
      <div className="border border-gray-300 bg-gray-50 p-4 mb-4">
        <Plot
          data={[
            {
              x: signalData.map((_, i) => i / sf),
              y: signalData,
              type: 'scatter',
              mode: 'lines',
              line: { color: 'rgb(73,68,68)', width: 0.5 },
            }
          ]}
          layout={{
            height: 300,
            margin: { l: 50, r: 20, t: 20, b: 40 },
            xaxis: {
              title: 'Time (s)',
              range: [
                Math.max(0, annotation.start - 2),
                Math.min(30, annotation.end + 2)
              ]
            },
            yaxis: { title: 'Amplitude' },
            shapes: [{
              type: 'rect',
              x0: annotation.start,
              x1: annotation.end,
              y0: Math.min(...signalData),
              y1: Math.max(...signalData),
              fillcolor: 'rgba(211,211,211,0.3)',
              line: { width: 1, color: 'rgb(73,68,68)' }
            }]
          }}
          config={{ displayModeBar: false, responsive: true }}
          useResizeHandler={true}
          key={`micro-${windowSize.width}-${windowSize.height}`}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between">
        <button
          onClick={() => navigate(`/analysis/${recordingId}/epoch/${epochId}`)}
          className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
        >
          Back to Epoch
        </button>
        <div className="space-x-2">
          <button
            onClick={() => {/* TODO: Implement edit */}}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Edit
          </button>
          <button
            onClick={handleDelete}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default MicroAnnotation; 